import { IconSelector } from "@tabler/icons-react";
import { useRef, type FC, type ReactNode } from "react";
import { Button, Group, Input } from "react-aria-components";
import type { Size } from "../helpers/helpers.js";
import { useComboboxAutoHighlight } from "./comboboxAutoHighlight.js";
import { useComboboxFieldBehavior } from "./comboboxFieldBehavior.js";
import { noteKeyboardInput } from "./comboboxRemoval.js";
import { comboboxStyle } from "./style.js";

export interface ComboboxInputProps {
  placeholder?: string;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  leftAccessory?: ReactNode;
  autoFocus?: boolean;
  className?: string;
}

/**
 * The text field of a single-select {@link Combobox}: an input that doubles as
 * the popup trigger, with a chevron button to toggle the full list. Clicking
 * anywhere in the field focuses the input and expands the popup; tabbing into
 * it does not expand (W3C combobox pattern).
 */
export const ComboboxInput: FC<ComboboxInputProps> = (props) => {
  const { placeholder, size, leftAccessory, autoFocus, className } = props;
  const style = comboboxStyle({ size });
  const inputRef = useRef<HTMLInputElement>(null);
  const { handleFieldPointerDown, handleFieldPointerUp, handleInputFocus } =
    useComboboxFieldBehavior(inputRef, { selectsValueOnFocus: true });
  const { activeDescendant, handleKeyDownCapture } = useComboboxAutoHighlight();

  return (
    <Group
      className={style.field({ className })}
      onPointerDown={handleFieldPointerDown}
      onPointerUp={handleFieldPointerUp}
    >
      {leftAccessory ? (
        <div className="shrink-0 text-gray-700">{leftAccessory}</div>
      ) : null}
      <Input
        ref={inputRef}
        className={style.input()}
        placeholder={placeholder}
        autoFocus={autoFocus}
        aria-activedescendant={activeDescendant}
        onFocus={handleInputFocus}
        onKeyDownCapture={(event) => {
          noteKeyboardInput();
          handleKeyDownCapture(event);
        }}
      />
      <Button className={style.trigger()}>
        <IconSelector className={style.chevron()} />
      </Button>
    </Group>
  );
};
